import React, { useState } from "react";
import PropTypes from "prop-types";
import NewTab from "@druva-apps/orion-icons/lib/NewtabGreyOutline";
import ChevronmediumdownGreyOutline from "@druva-apps/orion-icons/lib/ChevronsmalldownGreyOutline";
import ChevronsmallrightGreyOutline from "@druva-apps/orion-icons/lib/ChevronsmallrightGreyOutline";
import NavLink from "reactstrap/lib/NavLink";

import Popover from "../popover/popover";
import { isFunction, gatherClasses } from "../../lib/utils";
import "./megaMenu.scss";

const MegaMenu = props => {
  const { columns, title, disabled, placement = "bottom" } = props;
  const [isOpen, setIsOpen] = useState(false);

  const openPopup = () => {
    setIsOpen(true);
  };

  const closePopup = () => {
    setIsOpen(false);
  };

  const togglePopup = () => {
    setIsOpen(!isOpen);
  };
  const renderColumnHeaderContent = header => {
    return (
      <div className="oui-pb-4 oui-d-inline-flex oui-align-items-center oui-mega-menu-column__header">
        {header.icon}

        <span className="oui-mega-menu-column__header-title">
          {header.text}
        </span>

        {header.target === "_blank" ? (
          <NewTab
            muted={false}
            inheritFontSize
            inheritFontColor
            className="oui-ml-2"
          />
        ) : null}
      </div>
    );
  };

  const renderColumnHeader = column => {
    const { header } = column;

    if (!header) {
      return null;
    }

    if (header.href) {
      return (
        <a
          href={header.href}
          target={header.target}
          onClick={
            isFunction(header.onClick)
              ? e => {
                  e.preventDefault();
                  header.onClick(e, header);
                  closePopup();
                }
              : null
          }
        >
          {renderColumnHeaderContent(header)}
        </a>
      );
    }

    return renderColumnHeaderContent(header);
  };

  const findColumnSize = (links, chunkSize = 4) => {
    const columnChunk = [];

    for (let i = 0; i < links.length; i += chunkSize) {
      const chunk = links.slice(i, i + chunkSize);
      columnChunk.push(chunk);
    }

    return columnChunk;
  };

  // Render column body as links
  const renderColumnBodyLinks = column => {
    const { links, linksChunkSize } = column;

    if (!links) {
      return null;
    }

    const rowChunks = findColumnSize(links, linksChunkSize);
    return (
      <div className="oui-mega-menu-column__body oui-d-flex">
        {rowChunks.map((row, rowIndex) => (
          <div
            className="oui-mega-menu-column__body-content"
            key={`megamenu-body-${rowIndex}`}
          >
            {row.map((linkItem, ind) => {
              if (linkItem.children) {
                return (
                  <Popover
                    type="uncontrolled"
                    hideOnOutsideClick
                    hideArrow
                    key={`subPopover-${ind}`}
                    placement="right-start"
                    className="oui-megamenu-sublinks-popover"
                    trigger="click"
                    size="auto"
                    body={() => {
                      return (
                        <div>
                          {linkItem.children.map((item, i) => (
                            <a
                              href={item.href || "#"}
                              target={item.target || "_blank"}
                              rel="noreferrer noopener"
                              className={gatherClasses(null, "oui-d-block")}
                              aria-label={item.text}
                              key={`subPopoverLink-${i}`}
                              onClick={e => {
                                if (isFunction(item.onClick)) {
                                  e.preventDefault();
                                  item.onClick(e, item);
                                }
                              }}
                            >
                              <div className="oui-pb-2 oui-d-inline-flex oui-align-items-center">
                                {item.icon}
                                <span
                                  className={`oui-mega-menu-sublinks ${
                                    item.icon ? "oui-ml-2" : ""
                                  }`}
                                >
                                  {item.text}
                                </span>
                              </div>
                            </a>
                          ))}
                        </div>
                      );
                    }}
                  >
                    <a
                      href="/"
                      onClick={e => e.preventDefault()}
                      key={`children-${ind}`}
                    >
                      <div className="oui-pb-3 oui-d-inline-flex oui-align-items-end">
                        {linkItem.icon}
                        <span className="oui-mega-menu-links oui-ml-2">
                          {linkItem.text}
                        </span>
                        <ChevronsmallrightGreyOutline
                          size="sm"
                          className="oui-ml-1"
                        />
                      </div>
                    </a>
                  </Popover>
                );
              }
              return (
                <a
                  href={linkItem.href || "#"}
                  target={linkItem.target || "_blank"}
                  rel="noreferrer noopener"
                  key={`megamenu-link-${ind}`}
                  className={gatherClasses(null, "oui-d-block")}
                  aria-label={linkItem.text}
                  onClick={e => {
                    closePopup();
                    if (isFunction(linkItem.onClick)) {
                      e.preventDefault();
                      linkItem.onClick(e, linkItem);
                    }
                  }}
                >
                  <div className="oui-pb-3 oui-d-inline-flex oui-align-items-end">
                    {linkItem.icon}
                    <span className="oui-mega-menu-links oui-ml-2">
                      {linkItem.text}
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderColumnBody = column => {
    return isFunction(column.renderComponent)
      ? column.renderComponent(column, closePopup)
      : renderColumnBodyLinks(column);
  };

  const renderPopoverContent = () => {
    return (
      <div className="oui-mega-menu-body__fullbody-content oui-d-inline-flex">
        {columns.map((column, index) => (
          <React.Fragment key={`column-${index}`}>
            <div className="oui-mega-menu-column">
              {renderColumnHeader(column)}

              {renderColumnBody(column)}
            </div>
            {index !== columns.length - 1 && (
              <div
                className="oui-mega-menu-column-divider"
                key={`divider-${index}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderTrigger = () => {
    return (
      <NavLink
        className={gatherClasses(null, "oui-mega-menu")}
        href={props.href}
        onClick={e => {
          e.preventDefault();
          openPopup();
        }}
      >
        {title}&nbsp;
        <ChevronmediumdownGreyOutline
          inheritFontColor
          size="xs"
          width={11}
          height={11}
          muted={false}
          className="oui-dropdown-caret"
        />
      </NavLink>
    );
  };

  return (
    <Popover
      disabled={disabled}
      size="auto"
      type="controlled"
      isOpen={isOpen}
      toggle={togglePopup}
      hideOnOutsideClick
      hideArrow
      placement={placement}
      className="oui-mega-menu-popover"
      body={() => (
        <div className="oui-mega-menu-body__fullbody">
          {renderPopoverContent()}
        </div>
      )}
    >
      {renderTrigger()}
    </Popover>
  );
};

MegaMenu.propTypes = {
  placement: PropTypes.oneOf([
    "auto",
    "auto-start",
    "auto-end",
    "top",
    "top-start",
    "top-end",
    "right",
    "right-start",
    "right-end",
    "bottom",
    "bottom-start",
    "bottom-end",
    "left",
    "left-start",
    "left-end"
  ]),
  title: PropTypes.string,
  href: PropTypes.string,
  disabled: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.object,
      renderComponent: PropTypes.func,
      linksChunkSize: PropTypes.number,
      links: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
          target: PropTypes.string,
          onClick: PropTypes.func,
          href: PropTypes.string,
          icon: PropTypes.shape({
            src: PropTypes.string,
            classes: PropTypes.string
          })
        })
      )
    })
  )
};

export default MegaMenu;
